import { AxiosError, AxiosResponse } from 'axios';
import {
  initApi,
  userLocaleActions,
  userSharedSelectors
} from 'common/modules/ReactShared';
import { DEFAULT_LOCALE } from 'i18n/config';
import { feedbackContextCollector } from 'students/utils/feedbackContext';
import { authActions } from '../auth';
import { tAppDispatch } from '../rootStore';
import rootStore from '../rootStore';
import { listenFederatedAuthMessage } from './federatedAuth';
import { postAuthCompleteMessage } from './helpers';
import { reportAuthLoss } from './reportAuthLoss';

export function init(): (dispatch: tAppDispatch) => void {
  return (dispatch: tAppDispatch) => {
    initApi({
      onRejectResponse: apiErrorHandling(dispatch),
      onFulfilledResponse: apiResponseInterceptor(dispatch)
    });

    dispatch(authActions.checkAuth());
    dispatch(userLocaleActions.initLocale(DEFAULT_LOCALE));
    document.documentElement.setAttribute('lang', DEFAULT_LOCALE);

    listenFederatedAuthMessage(async () => {
      await dispatch(authActions.checkAuth());
      postAuthCompleteMessage();
    });
  };
}

// Exported for testing.
export function apiErrorHandling(dispatch: tAppDispatch) {
  return (error: AxiosError) => {
    const status = error.response?.status;
    // Read before the 401 handler below clears the profile.
    const signedIn = Boolean(userSharedSelectors.selectProfile(rootStore.getState()));
    reportAuthLoss(error, signedIn);
    if (status === 401) {
      dispatch(authActions.logOutStorageClear());
    }
    // TODO use server custom error codes like "error.response?.data.code === 10020"
    // else if (status === 403) {
    //   dispatch(showLessonLicenseModal());
    // }

    let errorMessage = '';
    try {
      const data = error.response?.data;
      if (data !== undefined) {
        errorMessage = JSON.stringify(data).slice(0, 300);
      }
    } catch {
      errorMessage = '<unserializable>';
    }

    feedbackContextCollector.pushApiError({
      method: error.config?.method?.toUpperCase() ?? '',
      url: (error.config?.url ?? '').split('?')[0],
      status: error.response?.status ?? null,
      statusText: error.response?.statusText ?? '',
      errorMessage,
      timestamp: new Date().toISOString()
    });

    return Promise.reject(error);
  };
}

// Exported for testing.
export function apiResponseInterceptor(dispatch: tAppDispatch) {
  return (response: AxiosResponse<any>) => {
    const state = rootStore.getState();
    const profile = userSharedSelectors.selectProfile(state);

    // TODO handle lang only once if necessary
    // set locale from API response header only if user is logged in.
    // A response without the header carries no locale information, so it must
    // leave the current locale alone instead of falling back to the default —
    // otherwise any such response silently switches the app's language.
    if (profile) {
      const apiLocale = response.headers.locale;

      if (apiLocale) {
        dispatch(userLocaleActions.initLocale(apiLocale));
        document.documentElement.setAttribute('lang', apiLocale);
      }
    }
    return response;
  };
}
